import React, { useEffect, useMemo, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Wind, BarChart3, Briefcase, ArrowRight, ArrowLeft, CheckCircle2, Globe, Zap, Activity, Table2, AlertTriangle, Sparkles, ArrowLeftRight, Check } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { useUserProfile } from '../hooks/useUserProfile';
// --- Constants & Data ---
type Role = 'energy_trader' | 'meteorologist' | 'analyst' | 'portfolio_manager' | '';
const ROLES = [{
  id: 'energy_trader',
  title: 'Energy Trader',
  icon: TrendingUp,
  desc: 'Intraday & day-ahead trading, price signals, spreads'
}, {
  id: 'meteorologist',
  title: 'Meteorologist',
  icon: Wind,
  desc: 'Weather forecasting, model analysis, accuracy tracking'
}, {
  id: 'analyst',
  title: 'Analyst',
  icon: BarChart3,
  desc: 'Market research, cross-border flows, reporting'
}, {
  id: 'portfolio_manager',
  title: 'Portfolio Manager',
  icon: Briefcase,
  desc: 'Asset optimization, risk management, P&L'
}];
const REGIONS = [{
  name: 'Central Europe',
  zones: ['DE-LU', 'FR', 'NL', 'AT', 'CH', 'BE']
}, {
  name: 'Nordics',
  zones: ['NO1', 'NO2', 'NO3', 'NO4', 'NO5', 'SE1', 'SE2', 'SE3', 'SE4', 'FI', 'DK1', 'DK2']
}, {
  name: 'Iberia & Italy',
  zones: ['ES', 'PT', 'IT-North', 'IT-South']
}, {
  name: 'UK & Ireland',
  zones: ['GB', 'IE']
}];
const JUA_MODELS = ['EPT-2', 'EPT-2 Reasoning', 'EPT-2 HRRR', 'EPT-2e'];
const EXTERNAL_MODELS = ['EC AIFS', 'EC IFS', 'GFS', 'ICON Global', 'ICON-EU', 'Microsoft Aurora', 'EC ENS'];
const ATHENA_PRESETS = [{
  id: 'tables',
  label: 'Always use tables for comparisons',
  icon: Table2
}, {
  id: 'deltas',
  label: 'Show run deltas (Δ) in all forecasts',
  icon: TrendingUp
}, {
  id: 'divergence',
  label: 'Flag model divergence >15% as ⚠ signal',
  icon: AlertTriangle
}, {
  id: 'confidence',
  label: 'Tag confidence levels (HIGH/MED/LOW)',
  icon: Sparkles
}, {
  id: 'spreads',
  label: 'Show DA vs ID price spreads',
  icon: BarChart3
}, {
  id: 'wind',
  label: 'Include wind capacity factors',
  icon: Wind
}, {
  id: 'flows',
  label: 'Include cross-border flow analysis',
  icon: ArrowLeftRight
}, {
  id: 'balancing',
  label: 'Show balancing market signals',
  icon: Activity
}];
// --- Helper Functions ---
function getCurrencyForZone(zone: string): string {
  if (zone.startsWith('GB')) return 'GBP';
  if (zone.startsWith('NO')) return 'NOK';
  if (zone.startsWith('SE')) return 'SEK';
  if (zone.startsWith('DK')) return 'DKK';
  if (zone === 'PL') return 'PLN';
  return 'EUR';
}
function getCalendarsForZone(zone: string): string[] {
  if (['DE-LU', 'FR', 'NL', 'BE', 'AT', 'CH'].includes(zone)) return ['EPEX'];
  if (zone.startsWith('NO') || zone.startsWith('SE') || zone.startsWith('DK') || zone === 'FI') return ['Nord Pool'];
  if (zone === 'GB') return ['N2EX'];
  if (zone.startsWith('IT')) return ['GME'];
  if (['ES', 'PT'].includes(zone)) return ['OMIE'];
  return ['EPEX'];
}
// --- Main Component ---
export function OnboardingView() {
  const navigate = useNavigate();
  const {
    profile,
    updateProfile,
    saveProfile
  } = useUserProfile();
  // --- State ---
  const [step, setStep] = useState(0);
  // Step 1: Identity
  const [role, setRole] = useState<Role>('');
  const [company, setCompany] = useState('');
  // Step 2: Markets
  const [primaryZone, setPrimaryZone] = useState('');
  const [secondaryZones, setSecondaryZones] = useState<string[]>([]);
  // Step 3: Data
  const [timeframe, setTimeframe] = useState<'6h' | '12h' | '24h' | '48h' | '7d'>('24h');
  const [tradingMarkets, setTradingMarkets] = useState<string[]>(['day_ahead']);
  const [models, setModels] = useState<string[]>(['EPT-2', 'EC AIFS']);
  const [units, setUnits] = useState<'metric' | 'imperial'>('metric');
  const [capacityDisplay, setCapacityDisplay] = useState<'absolute' | 'percentage'>('absolute');
  // Step 4: Athena
  const [responseStyle, setResponseStyle] = useState<'concise' | 'tables-only' | 'detailed'>('concise');
  const [presets, setPresets] = useState<Record<string, boolean>>({
    tables: true,
    deltas: true,
    divergence: true,
    confidence: true,
    spreads: false,
    wind: false,
    flows: false,
    balancing: false
  });
  // --- Smart Defaults Logic ---
  const handleRoleSelect = (selectedRole: Role) => {
    setRole(selectedRole);
    // Apply smart defaults based on role
    if (selectedRole === 'energy_trader') {
      setTimeframe('12h');
      setTradingMarkets(['intraday', 'day_ahead']);
      setModels(['EPT-2', 'EC AIFS']);
      setResponseStyle('concise');
      setPresets({
        tables: true,
        deltas: true,
        divergence: true,
        confidence: true,
        spreads: true,
        wind: false,
        flows: false,
        balancing: false
      });
    } else if (selectedRole === 'meteorologist') {
      setTimeframe('48h');
      setTradingMarkets(['day_ahead_plus', 'week_ahead']);
      setModels(['EPT-2', 'EC IFS', 'GFS', 'ICON-EU']);
      setResponseStyle('detailed');
      setPresets({
        tables: true,
        deltas: true,
        divergence: true,
        confidence: true,
        spreads: false,
        wind: true,
        flows: false,
        balancing: false
      });
    } else if (selectedRole === 'analyst') {
      setTimeframe('24h');
      setTradingMarkets(['day_ahead', 'day_ahead_plus']);
      setModels(['EPT-2', 'EC AIFS', 'EC IFS']);
      setResponseStyle('tables-only');
      setPresets({
        tables: true,
        deltas: true,
        divergence: false,
        confidence: true,
        spreads: true,
        wind: false,
        flows: true,
        balancing: false
      });
    } else if (selectedRole === 'portfolio_manager') {
      setTimeframe('7d');
      setTradingMarkets(['day_ahead', 'week_ahead', 'seasonal']);
      setModels(['EPT-2', 'EC AIFS']);
      setResponseStyle('concise');
      setPresets({
        tables: true,
        deltas: false,
        divergence: false,
        confidence: true,
        spreads: true,
        wind: false,
        flows: true,
        balancing: true
      });
    }
    // Auto-advance to next step
    setTimeout(() => {
      setDirection(1);
      setStep(1);
    }, 300);
  };
  const toggleSecondaryZone = (zone: string) => {
    if (zone === primaryZone) return;
    setSecondaryZones(prev => prev.includes(zone) ? prev.filter(z => z !== zone) : [...prev, zone]);
  };
  const toggleModel = (model: string) => {
    setModels(prev => prev.includes(model) ? prev.filter(m => m !== model) : [...prev, model]);
  };
  const toggleMarket = (market: string) => {
    setTradingMarkets(prev => {
      const next = prev.includes(market) ? prev.filter(m => m !== market) : [...prev, market];
      // Auto-set timeframe based on longest horizon selected
      const horizons: Record<string, '6h' | '12h' | '24h' | '48h' | '7d'> = {
        intraday: '6h',
        day_ahead: '12h',
        day_ahead_plus: '24h',
        week_ahead: '48h',
        seasonal: '7d'
      };
      const order = ['seasonal', 'week_ahead', 'day_ahead_plus', 'day_ahead', 'intraday'];
      const longest = order.find(o => next.includes(o));
      if (longest) setTimeframe(horizons[longest]);
      // Auto-select Jua models based on market horizons
      const shortTermMarkets = ['intraday', 'day_ahead', 'day_ahead_plus'];
      const longTermMarkets = ['week_ahead', 'seasonal'];
      const hasShortTerm = next.some(m => shortTermMarkets.includes(m));
      const hasLongTerm = next.some(m => longTermMarkets.includes(m));
      setModels(prevModels => {
        // Keep any external models the user already selected
        const externalModels = prevModels.filter(m => EXTERNAL_MODELS.includes(m));
        const juaModels: string[] = ['EPT-2']; // Always include EPT-2
        if (hasShortTerm) juaModels.push('EPT-2 Reasoning');
        if (hasLongTerm) juaModels.push('EPT-2e');
        // Deduplicate and merge
        return [...new Set([...juaModels, ...externalModels])];
      });
      return next;
    });
  };
  const togglePreset = (id: string) => {
    setPresets(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  // --- Navigation & Completion ---
  const nextStep = () => setStep(s => Math.min(s + 1, 4));
  const prevStep = () => setStep(s => Math.max(s - 1, 0));
  const handleComplete = () => {
    const currency = primaryZone ? getCurrencyForZone(primaryZone) : 'EUR';
    const calendars = primaryZone ? getCalendarsForZone(primaryZone) : ['EPEX'];
    // Map region based on primary zone
    let region = 'central-europe';
    if (primaryZone.startsWith('NO') || primaryZone.startsWith('SE') || primaryZone.startsWith('DK') || primaryZone === 'FI') region = 'nordics';
    if (primaryZone.startsWith('IT') || ['ES', 'PT'].includes(primaryZone)) region = 'iberia';
    if (['GB', 'IE'].includes(primaryZone)) region = 'uk-ireland';
    updateProfile({
      hasCompletedOnboarding: true,
      role: role || 'energy_trader',
      company,
      defaultBiddingZone: primaryZone || 'DE-LU',
      secondaryZones,
      priceCurrency: currency,
      tradingCalendars: calendars,
      defaultRegion: region,
      defaultChartTimeframe: timeframe,
      defaultModels: models,
      units,
      capacityDisplay,
      responseStyle,
      athenaPresets: presets
    });
    saveProfile();
    // Reset product tour so it shows after fresh onboarding
    localStorage.removeItem('jua-tour-completed');
    navigate('/athena', {
      state: {
        initialPrompt: 'What can Athena do?'
      }
    });
  };
  // --- Animation Variants ---
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  // Track direction for animations
  const [direction, setDirection] = useState(1);
  const goToStep = (newStep: number) => {
    setDirection(newStep > step ? 1 : -1);
    setStep(newStep);
  };
  // --- Render Steps ---
  const renderStep1 = () => <div className="space-y-8" data-id="element-4005">
      <div className="text-center space-y-2" data-id="element-4006">
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight" data-id="element-4007">
          What's your role?
        </h1>
        <p className="text-slate-400 text-lg" data-id="element-4008">
          Pick one and we'll set up your workspace instantly.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4009">
        {ROLES.map(r => <button key={r.id} onClick={() => handleRoleSelect(r.id as Role)} className={`relative flex flex-col items-start p-6 rounded-2xl border transition-all text-left overflow-hidden group ${role === r.id ? 'bg-[#EC4899]/10 border-[#EC4899] shadow-[0_0_30px_rgba(236,72,153,0.15)]' : 'bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.08] hover:border-white/[0.15]'}`} data-id="element-4010">
            {role === r.id && <div className="absolute top-4 right-4 text-[#EC4899]" data-id="element-4011">
                <CheckCircle2 size={20} className="fill-[#EC4899]/20" data-id="element-4012" />
              </div>}
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${role === r.id ? 'bg-[#EC4899]/20 text-[#EC4899]' : 'bg-white/[0.06] text-slate-400 group-hover:text-slate-200'}`} data-id="element-4013">
              <r.icon size={24} data-id="element-4014" />
            </div>
            <h3 className={`text-lg font-semibold mb-2 transition-colors ${role === r.id ? 'text-white' : 'text-slate-200'}`} data-id="element-4015">
              {r.title}
            </h3>
            <p className="text-sm text-slate-400 leading-relaxed" data-id="element-4016">{r.desc}</p>
          </button>)}
      </div>
    </div>;
  const renderStep2 = () => <div className="space-y-8" data-id="element-4017">
      <div className="text-center space-y-2" data-id="element-4018">
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight" data-id="element-4019">
          Where do you operate?
        </h1>
        <p className="text-slate-400 text-lg" data-id="element-4020">
          Select your primary bidding zone to set up market defaults.
        </p>
      </div>

      <div className="space-y-8 bg-white/[0.02] border border-white/[0.05] rounded-3xl p-6 md:p-8" data-id="element-4021">
        <div data-id="element-4022">
          <h3 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2" data-id="element-4023">
            <Globe size={16} className="text-[#EC4899]" data-id="element-4024" /> Select your bidding
            zones
          </h3>
          <p className="text-xs text-slate-500 mb-5" data-id="element-4025">
            The first zone you pick becomes your primary market. Any others will
            be monitored as secondary.
          </p>
          <div className="space-y-6" data-id="element-4026">
            {REGIONS.map(region => <div key={region.name} className="space-y-2" data-id="element-4027">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-4028">
                  {region.name}
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-4029">
                  {region.zones.map(zone => {
                const isPrimary = primaryZone === zone;
                const isSecondary = secondaryZones.includes(zone);
                return <button key={zone} onClick={() => {
                  if (isPrimary) {
                    // Deselect primary — promote first secondary if any
                    if (secondaryZones.length > 0) {
                      setPrimaryZone(secondaryZones[0]);
                      setSecondaryZones(prev => prev.slice(1));
                    } else {
                      setPrimaryZone('');
                    }
                  } else if (isSecondary) {
                    // Deselect secondary
                    setSecondaryZones(prev => prev.filter(z => z !== zone));
                  } else if (!primaryZone) {
                    // No primary yet — this becomes primary
                    setPrimaryZone(zone);
                  } else {
                    // Already have primary — add as secondary
                    setSecondaryZones(prev => [...prev, zone]);
                  }
                }} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all relative ${isPrimary ? 'bg-[#EC4899] text-white shadow-lg shadow-pink-500/25 border border-[#EC4899]' : isSecondary ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-[#1E293B] text-slate-300 border border-slate-700/60 hover:border-slate-500 hover:bg-slate-800'}`} data-id="element-4030">
                        {zone}
                        {isPrimary && <span className="ml-1.5 text-[10px] font-bold text-white/70" data-id="element-4031">
                            PRIMARY
                          </span>}
                      </button>;
              })}
                </div>
              </div>)}
          </div>
        </div>

        {primaryZone && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} className="pt-6 border-t border-white/[0.08]" data-id="element-4032">
            <div className="flex flex-wrap gap-4 p-4 rounded-xl bg-white/[0.03] border border-white/[0.05]" data-id="element-4033">
              <div className="flex items-center gap-2" data-id="element-4034">
                <span className="text-xs text-slate-500" data-id="element-4035">
                  Auto-configured Currency:
                </span>
                <Badge variant="outline" className="bg-slate-800/50" data-id="element-4036">
                  {getCurrencyForZone(primaryZone)}
                </Badge>
              </div>
              <div className="flex items-center gap-2" data-id="element-4037">
                <span className="text-xs text-slate-500" data-id="element-4038">
                  Trading Calendars:
                </span>
                <div className="flex gap-1" data-id="element-4039">
                  {getCalendarsForZone(primaryZone).map(cal => <Badge key={cal} variant="outline" className="bg-slate-800/50" data-id="element-4040">
                      {cal}
                    </Badge>)}
                </div>
              </div>
              {secondaryZones.length > 0 && <div className="flex items-center gap-2" data-id="element-4041">
                  <span className="text-xs text-slate-500" data-id="element-4042">Monitoring:</span>
                  <span className="text-xs text-blue-400 font-medium" data-id="element-4043">
                    +{secondaryZones.length} zone
                    {secondaryZones.length !== 1 ? 's' : ''}
                  </span>
                </div>}
            </div>
          </motion.div>}
      </div>
    </div>;
  const renderStep3 = () => <div className="space-y-8" data-id="element-4044">
      <div className="text-center space-y-2" data-id="element-4045">
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight" data-id="element-4046">
          Customize your workspace
        </h1>
        <p className="text-slate-400 text-lg" data-id="element-4047">
          We've pre-selected defaults based on your role, but you can adjust
          them.
        </p>
      </div>

      <div className="space-y-8 bg-white/[0.02] border border-white/[0.05] rounded-3xl p-6 md:p-8" data-id="element-4048">
        <div className="space-y-3" data-id="element-4049">
          <label className="text-sm font-medium text-slate-300" data-id="element-4050">
            Which markets do you trade?
          </label>
          <p className="text-xs text-slate-500" data-id="element-4051">
            Select all that apply — we'll optimize your default views
            accordingly.
          </p>
          <div className="flex flex-wrap gap-2 pt-1" data-id="element-4052">
            {[{
            id: 'intraday',
            label: 'Intraday',
            desc: '0–6h'
          }, {
            id: 'day_ahead',
            label: 'Day-Ahead',
            desc: '12–36h'
          }, {
            id: 'day_ahead_plus',
            label: 'Day-Ahead+',
            desc: '1–2 days'
          }, {
            id: 'week_ahead',
            label: 'Week-Ahead',
            desc: '2–7 days'
          }, {
            id: 'seasonal',
            label: 'Seasonal',
            desc: '7d+'
          }].map(market => <button key={market.id} onClick={() => toggleMarket(market.id)} className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-all flex items-center gap-2 ${tradingMarkets.includes(market.id) ? 'border-[#EC4899] bg-[#EC4899]/15 text-[#EC4899]' : 'border-slate-700/60 bg-[#1E293B] text-slate-400 hover:border-slate-600 hover:text-slate-300'}`} data-id="element-4053">
                {market.label}
                <span className={`text-[10px] font-normal ${tradingMarkets.includes(market.id) ? 'text-[#EC4899]/60' : 'text-slate-600'}`} data-id="element-4054">
                  {market.desc}
                </span>
              </button>)}
          </div>
        </div>

        <div className="space-y-4" data-id="element-4055">
          <label className="text-sm font-medium text-slate-300" data-id="element-4056">
            Preferred Models
          </label>

          <div className="space-y-3" data-id="element-4057">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-4058">
              Jua Models
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-4059">
              {JUA_MODELS.map(model => <button key={model} onClick={() => toggleModel(model)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${models.includes(model) ? 'border-[#EC4899] bg-[#EC4899]/15 text-[#EC4899]' : 'border-slate-700/60 bg-[#1E293B] text-slate-400 hover:border-slate-600 hover:text-slate-300'}`} data-id="element-4060">
                  {model}
                </button>)}
            </div>
          </div>

          <div className="space-y-3" data-id="element-4061">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-4062">
              External Models
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-4063">
              {EXTERNAL_MODELS.map(model => <button key={model} onClick={() => toggleModel(model)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${models.includes(model) ? 'border-blue-500 bg-blue-500/15 text-blue-400' : 'border-slate-700/60 bg-[#1E293B] text-slate-400 hover:border-slate-600 hover:text-slate-300'}`} data-id="element-4064">
                  {model}
                </button>)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-white/[0.08]" data-id="element-4065">
          <div className="space-y-3" data-id="element-4066">
            <label className="text-sm font-medium text-slate-300" data-id="element-4067">
              Measurement Units
            </label>
            <div className="flex rounded-xl overflow-hidden border border-slate-700/60 w-full bg-[#1E293B] p-1 gap-1" data-id="element-4068">
              <button onClick={() => setUnits('metric')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${units === 'metric' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-4069">
                Metric (°C, m/s)
              </button>
              <button onClick={() => setUnits('imperial')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${units === 'imperial' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-4070">
                Imperial (°F, mph)
              </button>
            </div>
          </div>

          <div className="space-y-3" data-id="element-4071">
            <label className="text-sm font-medium text-slate-300" data-id="element-4072">
              Capacity Display
            </label>
            <div className="flex rounded-xl overflow-hidden border border-slate-700/60 w-full bg-[#1E293B] p-1 gap-1" data-id="element-4073">
              <button onClick={() => setCapacityDisplay('absolute')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${capacityDisplay === 'absolute' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-4074">
                Absolute (GW)
              </button>
              <button onClick={() => setCapacityDisplay('percentage')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${capacityDisplay === 'percentage' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-4075">
                Factor (%)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
  const renderStep4 = () => <div className="space-y-8" data-id="element-4076">
      <div className="text-center space-y-2" data-id="element-4077">
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight" data-id="element-4078">
          Configure your AI assistant
        </h1>
        <p className="text-slate-400 text-lg" data-id="element-4079">
          How should Athena communicate with you?
        </p>
      </div>

      <div className="space-y-8" data-id="element-4080">
        <div className="space-y-4" data-id="element-4081">
          <label className="text-sm font-medium text-slate-300" data-id="element-4082">
            Response Style
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4083">
            {[{
            id: 'concise',
            icon: '⚡',
            title: 'Concise',
            desc: 'Quick answers, key numbers, actionable signals',
            preview: 'Wind generation in DE-LU will peak at 24.5 GW tomorrow at 14:00 (+2.1 GW vs previous run).'
          }, {
            id: 'tables-only',
            icon: '📊',
            title: 'Tables Only',
            desc: 'Structured data tables, comparison matrices',
            preview: 'Model | 12:00 | 13:00 | 14:00\nEPT-2 | 22.1 | 23.4 | 24.5\nEC IFS| 21.8 | 22.9 | 23.8'
          }, {
            id: 'detailed',
            icon: '📝',
            title: 'Detailed',
            desc: 'Full analysis with context, reasoning, and recommendations',
            preview: 'The latest EPT-2 run shows a significant upward revision for DE-LU wind generation tomorrow. This is driven by a stronger than expected low-pressure system moving across the North Sea...'
          }].map(style => <button key={style.id} onClick={() => setResponseStyle(style.id as any)} className={`flex flex-col items-start p-5 rounded-2xl border transition-all text-left ${responseStyle === style.id ? 'bg-[#EC4899]/10 border-[#EC4899] shadow-[0_0_20px_rgba(236,72,153,0.1)]' : 'bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.08] hover:border-white/[0.15]'}`} data-id="element-4084">
                <div className="flex items-center gap-2 mb-2" data-id="element-4085">
                  <span className="text-xl" data-id="element-4086">{style.icon}</span>
                  <h3 className={`font-semibold ${responseStyle === style.id ? 'text-white' : 'text-slate-200'}`} data-id="element-4087">
                    {style.title}
                  </h3>
                </div>
                <p className="text-xs text-slate-400 mb-4 h-8" data-id="element-4088">{style.desc}</p>
                <div className="w-full bg-black/40 rounded-lg p-3 border border-white/[0.05]" data-id="element-4089">
                  {style.id === 'tables-only' ? <table className="w-full text-[10px] font-mono text-slate-500" data-id="element-4090">
                      <thead data-id="element-4091">
                        <tr className="border-b border-white/[0.08]" data-id="element-4092">
                          <th className="text-left py-1 pr-2 font-semibold text-slate-400" data-id="element-4093">
                            Model
                          </th>
                          <th className="text-right py-1 px-1 font-semibold text-slate-400" data-id="element-4094">
                            12:00
                          </th>
                          <th className="text-right py-1 px-1 font-semibold text-slate-400" data-id="element-4095">
                            13:00
                          </th>
                          <th className="text-right py-1 pl-1 font-semibold text-slate-400" data-id="element-4096">
                            14:00
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-4097">
                        <tr className="border-b border-white/[0.04]" data-id="element-4098">
                          <td className="py-1 pr-2 text-[#EC4899]" data-id="element-4099">EPT-2</td>
                          <td className="text-right py-1 px-1" data-id="element-4100">22.1</td>
                          <td className="text-right py-1 px-1" data-id="element-4101">23.4</td>
                          <td className="text-right py-1 pl-1 text-slate-300" data-id="element-4102">
                            24.5
                          </td>
                        </tr>
                        <tr data-id="element-4103">
                          <td className="py-1 pr-2 text-blue-400" data-id="element-4104">EC IFS</td>
                          <td className="text-right py-1 px-1" data-id="element-4105">21.8</td>
                          <td className="text-right py-1 px-1" data-id="element-4106">22.9</td>
                          <td className="text-right py-1 pl-1 text-slate-300" data-id="element-4107">
                            23.8
                          </td>
                        </tr>
                      </tbody>
                    </table> : <p className="text-[10px] text-slate-500 font-mono leading-relaxed whitespace-pre-wrap line-clamp-3" data-id="element-4108">
                      {style.preview}
                    </p>}
                </div>
              </button>)}
          </div>
        </div>

        <div className="space-y-4 pt-4 border-t border-white/[0.08]" data-id="element-4109">
          <div className="flex items-center justify-between" data-id="element-4110">
            <label className="text-sm font-medium text-slate-300" data-id="element-4111">
              Auto-Include Presets
            </label>
            <span className="text-xs text-slate-500" data-id="element-4112">
              Pre-selected for{' '}
              {ROLES.find(r => r.id === role)?.title || 'your role'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4113">
            {ATHENA_PRESETS.map(preset => <button key={preset.id} onClick={() => togglePreset(preset.id)} className={`flex items-center justify-between p-3.5 rounded-xl border transition-colors ${presets[preset.id] ? 'bg-slate-800/80 border-slate-700' : 'bg-[#1E293B]/50 border-slate-800 hover:bg-[#1E293B]'}`} data-id="element-4114">
                <div className="flex items-center gap-3" data-id="element-4115">
                  <preset.icon size={16} className={presets[preset.id] ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-4116" />
                  <span className={`text-sm font-medium ${presets[preset.id] ? 'text-white' : 'text-slate-400'}`} data-id="element-4117">
                    {preset.label}
                  </span>
                </div>
                <div className={`w-10 h-5 rounded-full p-0.5 transition-colors ${presets[preset.id] ? 'bg-[#EC4899]' : 'bg-slate-700'}`} data-id="element-4118">
                  <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${presets[preset.id] ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-4119" />
                </div>
              </button>)}
          </div>
        </div>
      </div>
    </div>;
  const renderStep5 = () => <div className="space-y-10 text-center" data-id="element-4120">
      <div className="space-y-4" data-id="element-4121">
        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#f0407a] to-[#a855f7] rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(236,72,153,0.4)] mb-6" data-id="element-4122">
          <Check size={40} className="text-white" data-id="element-4123" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight" data-id="element-4124">
          You're all set
        </h1>
        <p className="text-slate-400 text-lg max-w-md mx-auto" data-id="element-4125">
          Your workspace has been configured. You can always adjust these
          preferences in Settings.
        </p>
      </div>

      <div className="max-w-md mx-auto bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 text-left space-y-4" data-id="element-4126">
        <div className="flex items-center justify-between pb-4 border-b border-white/[0.08]" data-id="element-4127">
          <span className="text-slate-400 text-sm" data-id="element-4128">Role</span>
          <Badge variant="pink" className="bg-[#EC4899]/20 text-[#EC4899] border-none" data-id="element-4129">
            {ROLES.find(r => r.id === role)?.title}
          </Badge>
        </div>
        <div className="flex items-center justify-between pb-4 border-b border-white/[0.08]" data-id="element-4130">
          <span className="text-slate-400 text-sm" data-id="element-4131">Primary Market</span>
          <span className="text-white font-medium" data-id="element-4132">
            {primaryZone || 'DE-LU'}
          </span>
        </div>
        <div className="flex items-center justify-between pb-4 border-b border-white/[0.08]" data-id="element-4133">
          <span className="text-slate-400 text-sm" data-id="element-4134">Timeframe</span>
          <span className="text-white font-medium" data-id="element-4135">{timeframe}</span>
        </div>
        <div className="flex items-center justify-between pb-4 border-b border-white/[0.08]" data-id="element-4136">
          <span className="text-slate-400 text-sm" data-id="element-4137">Active Models</span>
          <span className="text-white font-medium" data-id="element-4138">
            {models.length} selected
          </span>
        </div>
        <div className="flex items-center justify-between" data-id="element-4139">
          <span className="text-slate-400 text-sm" data-id="element-4140">Athena Style</span>
          <span className="text-white font-medium capitalize" data-id="element-4141">
            {responseStyle.replace('-', ' ')}
          </span>
        </div>
      </div>

      <div className="pt-4" data-id="element-4142">
        <Button size="md" className="px-8 gap-2 font-semibold shadow-sm hover:shadow-md transition-all" onClick={handleComplete} data-id="element-4143">
          Launch Jua <ArrowRight size={16} data-id="element-4144" />
        </Button>
      </div>
    </div>;
  const steps = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];
  // --- Main Render ---
  return <div className="min-h-screen bg-[#0B1120] text-slate-100 font-sans selection:bg-pink-500/30 relative overflow-hidden flex flex-col" data-id="element-4145">
      {/* Background Gradients */}
      <div className="absolute inset-x-0 top-0 h-[800px] bg-gradient-to-b from-[#1a0a12] via-[#0B1120] to-[#0B1120] pointer-events-none" data-id="element-4146" />
      <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-pink-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-4147" />
      <div className="absolute top-[-100px] right-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px] pointer-events-none" data-id="element-4148" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-8 py-6" data-id="element-4149">
        <div className="flex items-center gap-2 opacity-80" data-id="element-4150">
          <img src="https://cdn.magicpatterns.com/uploads/iXnnzvi4wVFko9E21giff4/pasted-image.png" alt="Jua logo" className="w-7 h-7 object-contain flex-shrink-0" data-id="element-4151" />
          <span className="text-lg font-light tracking-tight text-white" data-id="element-4152">
            Jua<span className="text-[#EC4899]" data-id="element-4153">.ai</span>
          </span>
        </div>
        {/* Progress Indicator */}
        <div className="flex flex-col items-center gap-2" data-id="element-4154">
          <div className="w-48 h-1 bg-white/[0.08] rounded-full overflow-hidden" data-id="element-4155">
            <motion.div className="h-full bg-[#EC4899]" initial={{
            width: '20%'
          }} animate={{
            width: `${(step + 1) / 5 * 100}%`
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} data-id="element-4156" />
          </div>
          <div className="flex gap-1.5" data-id="element-4157">
            {[0, 1, 2, 3, 4].map(i => <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${i <= step ? 'bg-[#EC4899]' : 'bg-white/[0.15]'}`} data-id="element-4158" />)}
          </div>
        </div>
        <button onClick={handleComplete} className="w-24 text-xs font-medium text-slate-500 hover:text-slate-300 transition-colors text-right" data-id="element-4159">
          Skip all →
        </button>
      </header>

      {/* Main Content Area */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-8" data-id="element-4160">
        <div className="w-full max-w-3xl relative min-h-[500px] flex flex-col" data-id="element-4161">
          <AnimatePresence mode="wait" custom={direction} data-id="element-4162">
            <motion.div key={step} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} className="w-full" data-id="element-4163">
              {steps[step]()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer Navigation */}
      {step < 4 && <footer className="relative z-10 px-8 py-6 flex items-center justify-between max-w-5xl mx-auto w-full border-t border-white/[0.05]" data-id="element-4164">
          <div data-id="element-4165">
            {step > 0 && <Button variant="ghost" onClick={() => goToStep(step - 1)} className="text-slate-400 hover:text-white gap-2" data-id="element-4166">
                <ArrowLeft size={16} data-id="element-4167" /> Back
              </Button>}
          </div>

          <div className="flex items-center gap-4" data-id="element-4168">
            {(step === 2 || step === 3) && <button onClick={() => goToStep(step + 1)} className="text-sm font-medium text-slate-500 hover:text-slate-300 transition-colors" data-id="element-4169">
                Skip for now
              </button>}

            <Button onClick={() => goToStep(step + 1)} disabled={step === 0 && !role || step === 1 && !primaryZone} className="gap-2 px-8" data-id="element-4170">
              Continue <ArrowRight size={16} data-id="element-4171" />
            </Button>
          </div>
        </footer>}
    </div>;
}